import { List } from 'lucide-react';
import { useMemo } from 'react';
import { unionBy } from 'lodash';

import { Environment } from '@/react/portainer/environments/types';
import { useEnvironmentList } from '@/react/portainer/environments/queries';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';
import { withMeta } from '@@/datatables/extend-options/withMeta';
import { mergeOptions } from '@@/datatables/extend-options/mergeOptions';

import { EdgeJob, JobResult, LogsStatus } from '../../types';
import { useJobResults } from '../../queries/jobResults/useJobResults';
import { useEdgeJob } from '../../queries/useEdgeJob';

import { columns } from './columns';
import { createStore } from './datatable-store';

const tableKey = 'edge-job-results';
const store = createStore(tableKey);

export function ResultsDatatable({ jobId }: { jobId: EdgeJob['Id'] }) {
  const tableState = useTableState(store, tableKey);

  const jobResultsQuery = useJobResults(jobId, {
    refetchInterval(dataset) {
      const anyCollecting = dataset?.some(
        (r) => r.LogsStatus === LogsStatus.Pending
      );

      if (anyCollecting) {
        return 5000;
      }

      return tableState.autoRefreshRate * 1000;
    },
  });

  // Get edge job details to access EdgeGroups and individual Endpoints
  const edgeJobQuery = useEdgeJob(jobId);

  // Check what the job targets
  const hasEdgeGroups = (edgeJobQuery.data?.EdgeGroups?.length ?? 0) > 0;
  const hasIndividualEndpoints =
    (edgeJobQuery.data?.Endpoints?.length ?? 0) > 0;

  // Fetch environments from edge groups if the job has any
  const edgeGroupEnvironmentsQuery = useEnvironmentList(
    {
      edgeGroupIds: edgeJobQuery.data?.EdgeGroups || [],
      pageLimit: 0,
      excludeSnapshots: true,
    },
    {
      enabled: hasEdgeGroups,
    }
  );

  // Fetch individual environments if the job has any
  const individualEndpointIds = useMemo(() => {
    if (!hasIndividualEndpoints || !edgeJobQuery.data?.Endpoints) {
      return [];
    }
    // edgeJobQuery.data.Endpoints is Array<EnvironmentId> from the API
    return edgeJobQuery.data.Endpoints;
  }, [hasIndividualEndpoints, edgeJobQuery.data?.Endpoints]);

  const individualEnvironmentsQuery = useEnvironmentList(
    {
      endpointIds: individualEndpointIds,
      excludeSnapshots: true,
    },
    {
      enabled: individualEndpointIds.length > 0,
    }
  );

  // Merge environments from both sources and deduplicate
  const allEnvironments = useMemo(() => {
    const edgeGroupEnvs = edgeGroupEnvironmentsQuery.environments || [];
    const individualEnvs = individualEnvironmentsQuery.environments || [];

    // Deduplicate by environment ID using lodash unionBy
    return unionBy(edgeGroupEnvs, individualEnvs, 'Id');
  }, [
    edgeGroupEnvironmentsQuery.environments,
    individualEnvironmentsQuery.environments,
  ]);

  const isLoading =
    jobResultsQuery.isLoading ||
    edgeJobQuery.isLoading ||
    (hasEdgeGroups && edgeGroupEnvironmentsQuery.isLoading) ||
    (hasIndividualEndpoints && individualEnvironmentsQuery.isLoading);

  const dataset = useMemo(
    () =>
      isLoading
        ? []
        : associateEndpointsToResults(
            jobResultsQuery.data || [],
            allEnvironments
          ),
    [allEnvironments, isLoading, jobResultsQuery.data]
  );

  return (
    <Datatable
      disableSelect
      columns={columns}
      dataset={dataset}
      isLoading={isLoading}
      title="Results"
      titleIcon={List}
      settingsManager={tableState}
      extendTableOptions={mergeOptions(
        withMeta({
          table: 'edge-job-results',
          jobId,
        })
      )}
      data-cy="edge-job-results-datatable"
    />
  );
}

function associateEndpointsToResults(
  results: Array<JobResult>,
  environments: Array<Environment>
) {
  return results.map((result) => {
    const environment = environments.find(
      (environment) => environment.Id === result.EndpointId
    );
    return {
      ...result,
      Endpoint: environment,
    };
  });
}
